/* Maps the raw engine output onto the governed Narrative Unit schema.
   The engine trims quotes for display; here every quote is restored to the
   verbatim sentence so the "carries the sentence it came from" invariant holds
   literally, not approximately. */

import NS, { type RawSource, type RawUnit } from '../engine.js';
import type { NarrativeUnit, Evidence } from './types.generated.ts';

export const SCHEMA_VERSION = '1.0.0';

export interface NormaliseOptions {
  title: string;
  question?: string | null;
  release: { id: string; version: string; vocabulary_version?: number };
  created_at?: string;
  sourceScopes?: Record<string, 'project' | 'analysis'>;
}

interface Sent { text: string; lc: string; source: string }

function corpusSentences(sources: RawSource[]): Sent[] {
  const out: Sent[] = [];
  for (const s of sources) for (const t of NS.sentences(s.text)) out.push({ text: t, lc: t.toLowerCase(), source: s.name });
  return out;
}

/** First sentence containing any of the lexicon terms. */
function firstWith(sents: Sent[], terms: string[]): Evidence | null {
  for (const s of sents) for (const t of terms) if (s.lc.includes(t.toLowerCase())) return { quote: s.text, source: s.source };
  return null;
}

/** Recover the untrimmed sentence behind a display quote ("…" suffix, 240 chars). */
function untrim(quote: string, source: string, sents: Sent[]): Evidence {
  const head = quote.replace(/…$/, '').trim();
  const norm = (x: string) => x.replace(/\s+/g, ' ');
  const hit = sents.find(s => s.source === source && norm(s.text).startsWith(norm(head)))
    ?? sents.find(s => norm(s.text).startsWith(norm(head)))
    ?? sents.find(s => norm(s.text).includes(norm(head).slice(0, 60)));
  return hit ? { quote: hit.text, source: hit.source } : { quote: head || quote, source };
}

export function normalise(raw: RawUnit, sources: RawSource[], opts: NormaliseOptions): NarrativeUnit {
  const sents = corpusSentences(sources);
  const fallback: Evidence = sents[0] ? { quote: sents[0].text, source: sents[0].source } : { quote: '(no sentence of 25+ characters in the sources)', source: sources[0]?.name ?? 'sources' };

  const frames = raw.meaning.frames.map(f => ({
    name: f.name,
    dominant: !!f.dominant,
    description: f.desc,
    markers: f.hits,
    sources: f.sources,
    confidence: f.confidence,
    inference: f.inference,
    evidence: firstWith(sents, NS.FRAMES[f.name] ?? []) ?? untrim(f.quote, f.src, sents),
  }));

  const actors = raw.landscape.actors.map(a => ({
    name: a.name,
    mentions: a.mentions,
    position: a.position,
    evidence: firstWith(sents, [a.name]) ?? (a.position ? untrim(a.position, sources[0]?.name ?? '', sents) : fallback),
  }));

  const absent = raw.landscape.absent.map(a => ({
    name: a.name,
    why: a.why,
    evidence: firstWith(sents, [a.name]) ?? fallback,
  }));

  const architectures = raw.architecture.architectures.map(a => ({
    name: a.name,
    status: (a.provisional ? 'provisional' : 'stable') as 'provisional' | 'stable',
    markers: a.hits,
    inference: !sents.some(s => s.lc.includes(a.name.toLowerCase())),
    evidence: firstWith(sents, NS.ARCHS[a.name] ?? []) ?? (a.note ? untrim(a.note, sources[0]?.name ?? '', sents) : fallback),
  }));

  const loops = raw.architecture.loops.map(l => ({
    name: l.name,
    type: l.type,
    closes: l.closes,
    corrected_by_user: !!l.corrected,
    confidence: (l.closes ? 'medium' : 'low') as 'medium' | 'low',
    inference: false,
    evidence: untrim(l.quote, l.src, sents),
  }));

  const pathways = raw.pathways.items.map(p => ({
    name: p.name,
    typology: p.typology,
    maturity: p.maturity,
    enabling_forces: p.enabling,
    constraining_forces: p.constraining,
    confidence: p.confidence,
    inference: !sents.some(s => s.lc.includes(p.typology.toLowerCase())),
    corrected_by_user: !!p.corrected,
    evidence: untrim(p.quote, p.src, sents),
  }));

  const emerging = raw.emerging.items.map(e => ({
    name: e.name,
    description: e.desc,
    confidence: e.confidence,
    inference: true,
    evidence: untrim(e.quote, e.src, sents),
  }));

  const opportunities = raw.opportunities.items.map(o => {
    // Each opportunity is derived from one finding; carry that finding's sentence.
    let evidence: Evidence | undefined;
    if (/^Give /.test(o.name) && absent[0]) evidence = absent[0].evidence;
    else if (/dominant loop/.test(o.name) && loops[0]) evidence = loops[0].evidence;
    else if (frames[0]) evidence = frames[0].evidence;
    return {
      steering_logic: o.logic.toLowerCase(),
      name: o.name,
      description: o.desc,
      inference: true,
      evidence: evidence ?? fallback,
    };
  });

  const unit: NarrativeUnit = {
    schema_version: SCHEMA_VERSION,
    title: opts.title,
    question: opts.question ?? null,
    release: opts.release,
    created_at: opts.created_at ?? new Date().toISOString(),
    sources: raw.sources.map(s => ({ name: s.name, kind: s.kind, words: s.words, scope: opts.sourceScopes?.[s.name] ?? 'analysis' })),
    corpus: { words: raw.stats.words, sentences: raw.stats.sentences, sources: raw.stats.sourceCount },
    meaning: { confidence: raw.meaning.confidence, frames },
    landscape: { confidence: raw.landscape.confidence, actors, absent },
    architecture: { confidence: raw.architecture.confidence, invisible_architectures: architectures, feedback_loops: loops },
    pathways: { confidence: raw.pathways.confidence, items: pathways },
    emerging_issues: { confidence: raw.emerging.confidence, items: emerging },
    transformation_opportunities: { confidence: raw.opportunities.confidence, items: opportunities },
    dark_spots: raw.darkSpots,
    stam_distribution: raw.stam,
    evolution: [],
    corrections: [],
    provenance: {
      method: 'deterministic',
      model: null,
      passes: ['meaning', 'landscape', 'architecture', 'pathways', 'emerging_issues', 'transformation_opportunities', 'dark_spots']
        .map((layer, i) => ({ id: `P${i + 1}`, layer, method: 'deterministic' as const, prompt_version: null, gates: 'passed' as const, note: null })),
    },
  };
  return unit;
}

/** One call: raw sources in, governed unit out. */
export function analyseToUnit(sources: RawSource[], opts: NormaliseOptions): NarrativeUnit {
  return normalise(NS.analyse(sources), sources, opts);
}
